import { IconExternalLink } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  Heading,
  LoadingIndicator,
  Text,
} from '@cfa/react-core';

import { PathwayModal } from '@/components/popups/PathwayModal';

export interface PrintReportConfigureStepProps {
  selectedLocations: string[];
  locationOptions: string[];
  onLocationsChange: (locations: string[]) => void;
  noLocationsSelected: boolean;
  isFoodSafety: boolean;
  showDoB: boolean;
  onShowDoBChange: (show: boolean) => void;
  onClose: () => void;
  onCreatePdf: () => void;
}

export const PrintReportConfigureStep: React.FC<
  PrintReportConfigureStepProps
> = ({
  selectedLocations,
  locationOptions,
  onLocationsChange,
  noLocationsSelected,
  isFoodSafety,
  showDoB,
  onShowDoBChange,
  onClose,
  onCreatePdf,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <PathwayModal.Header>
        <PathwayModal.Title>{t('Generic.printReport')}</PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body className="items-stretch! [--dialog-padding:20px_32px_24px]!">
        <Text className="text-grayscale-gray6! text-center">
          {t('TrainingPlans.chooseWhatToInclude')}
        </Text>

        <Divider className="my-2!" />

        {locationOptions.length ? (
          <CheckboxGroup
            className="text-left! [&>span]:text-base!"
            label={t('Generic.locations')}
            value={selectedLocations}
            onChange={onLocationsChange}
            isInvalid={noLocationsSelected && !selectedLocations.length}
            errorMessage={t('Generic.selectAtLeastOne')}
          >
            {locationOptions.map(location => (
              <Checkbox
                key={location}
                value={location}
                className="text-base! font-normal! [&>label]:flex! [&>label]:items-center!"
              >
                {location}
              </Checkbox>
            ))}
          </CheckboxGroup>
        ) : (
          <Text className="text-grayscale-gray6! text-center italic!">
            {t('TrainingPlans.noOptionsAvailable')}
          </Text>
        )}

        {isFoodSafety && (
          <>
            <Heading
              level={3}
              className="text-grayscale-gray6! bold! mt-4! text-left! font-bold!"
            >
              {t('Generic.options')}
            </Heading>
            <Checkbox
              isSelected={showDoB}
              onChange={onShowDoBChange}
              className={
                'text-base! font-normal! [&>label]:flex! [&>label]:items-center!'
              }
            >
              {t('Generic.showDoB')}
            </Checkbox>
          </>
        )}

        <Divider className="my-2!" />
      </PathwayModal.Body>
      <PathwayModal.Footer>
        <div className="flex w-full flex-col-reverse items-center justify-center gap-4 md:flex-row">
          <Button
            className="min-w-40!"
            color="secondary"
            onPress={onClose}
            variant="outlined"
          >
            {t('Button.cancel')}
          </Button>
          <Button
            className="min-w-40!"
            color="secondary"
            onPress={onCreatePdf}
            variant="filled"
          >
            {t('Button.createPdf')}
          </Button>
        </div>
      </PathwayModal.Footer>
    </>
  );
};

export const PrintReportLoadingStep: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PathwayModal.Body noMinHeight>
      <div className="flex flex-col items-center justify-center gap-4">
        <LoadingIndicator size="md" />
        <Text className="text-grayscale-gray7! text-center!">
          {t('Generic.creatingPdf')}
        </Text>
      </div>
    </PathwayModal.Body>
  );
};

export interface PrintReportReadyStepProps {
  onPrint: () => void;
}

export const PrintReportReadyStep: React.FC<PrintReportReadyStepProps> = ({
  onPrint,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <PathwayModal.Header>
        <PathwayModal.Title>{t('Generic.reportReady')}</PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight className="items-center! justify-center!">
        <div className="flex flex-col items-center gap-4">
          <Text className="text-grayscale-gray6! text-center!">
            {t('Generic.saveOrPrint')}
          </Text>
          <Button color="secondary" onPress={onPrint} variant="filled">
            <IconExternalLink className="mr-2 size-5" />
            {t('Button.viewAndPrint')}
          </Button>
        </div>
      </PathwayModal.Body>
    </>
  );
};
